/**
 * Geometry for the map tool overlay: tool state in, GeoJSON out.
 *
 * Pure functions, no React and no MapLibre, so the shapes can be reasoned about
 * (and the arithmetic checked) without a map in the room. This generalises
 * `buildDrawFeatures` in WeatherMap, which only ever had to draw one polygon.
 */

import type { Feature, FeatureCollection } from 'geojson';
import {
  etaAlongLine,
  etaToPoint,
  etaToZone,
  formatDistance,
  formatEtaRange,
  haversineMiles,
  type AreaGeometry,
  type EtaResult,
  type StormMotion,
  type TenantAlert,
} from '@ww/shared';

export type ToolMode = 'none' | 'eta' | 'storm-path' | 'measure' | 'zone-circle' | 'zone-polygon' | 'markup';

export type LngLat = [number, number];

export interface Stroke {
  id: string;
  points: LngLat[];
}

export interface DrawnZone {
  id: string;
  kind: 'circle' | 'polygon';
  /** Circle: [centre]. Polygon: the ring vertices, unclosed. */
  points: LngLat[];
  radiusMiles?: number;
}

export interface ToolState {
  mode: ToolMode;
  /**
   * A storm the user tapped to project. Session-only, never persisted: a storm-relative
   * projection is worthless five minutes later, and restoring one on reload would show a
   * confident, stale picture.
   */
  pathPick: { point: LngLat; alertId: string | null } | null;
  /**
   * A track the user aimed by hand, in compass degrees TOWARD, overriding the observed
   * storm motion for the arrival pin. Null means "use what the weather is actually
   * doing". Speed is never overridden — only the direction.
   */
  manualBearing: number | null;
  /** Pin for the ETA tool. */
  pin: LngLat | null;
  /** Measure line endpoints, at most two. */
  measure: LngLat[];
  /** Vertices of the zone currently being drawn. */
  pending: LngLat[];
  zones: DrawnZone[];
  strokes: Stroke[];
}

export const EMPTY_TOOL_STATE: ToolState = {
  mode: 'none',
  pathPick: null,
  manualBearing: null,
  pin: null,
  measure: [],
  pending: [],
  zones: [],
  strokes: [],
};

export const TOOL_COLOR = '#ffb454';
export const ZONE_COLOR = '#4fd6ef';
export const ZONE_ALERT_COLOR = '#ffa857';
export const MARKUP_COLOR = '#ff7a7f';
/** A hand-aimed track is drawn differently so it never passes for the observed one. */
export const MANUAL_COLOR = '#cbd5e8';

const EMPTY: FeatureCollection = { type: 'FeatureCollection', features: [] };

/** A circle as a 48-sided ring, in true miles rather than degrees. */
export function circleRing(centre: LngLat, radiusMiles: number, steps = 48): LngLat[] {
  const milesPerLat = 69.0;
  const milesPerLon = 69.172 * Math.cos((centre[1] * Math.PI) / 180) || 1e-6;
  const ring: LngLat[] = [];
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * 2 * Math.PI;
    ring.push([centre[0] + (Math.cos(a) * radiusMiles) / milesPerLon, centre[1] + (Math.sin(a) * radiusMiles) / milesPerLat]);
  }
  return ring;
}

export function zoneGeometry(zone: DrawnZone): AreaGeometry | null {
  if (zone.kind === 'circle') {
    const centre = zone.points[0];
    if (!centre || !zone.radiusMiles) return null;
    return { type: 'Polygon', coordinates: [circleRing(centre, zone.radiusMiles)] } as AreaGeometry;
  }
  if (zone.points.length < 3) return null;
  return { type: 'Polygon', coordinates: [[...zone.points, zone.points[0]!]] } as AreaGeometry;
}

/** Point `miles` away from `from` along a compass bearing. */
export function destination(from: LngLat, bearingDeg: number, miles: number): LngLat {
  const milesPerLat = 69.0;
  const milesPerLon = 69.172 * Math.cos((from[1] * Math.PI) / 180) || 1e-6;
  const rad = (bearingDeg * Math.PI) / 180;
  return [from[0] + (Math.sin(rad) * miles) / milesPerLon, from[1] + (Math.cos(rad) * miles) / milesPerLat];
}

/** Midpoint of a segment, for hanging a label on it. */
function midpoint(a: LngLat, b: LngLat): LngLat {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

function feat(geometry: Feature['geometry'], properties: Record<string, unknown>): Feature {
  return { type: 'Feature', geometry, properties };
}

export interface ToolRenderInput {
  state: ToolState;
  /** The alert driving ETAs — normally the soonest-arriving one. */
  alert?: { geometry: AreaGeometry | null; motion: StormMotion | null } | null;
  now: string;
}

export interface ToolRender {
  features: FeatureCollection;
  /** Readouts for the panel, already worded. */
  pinEta: EtaResult | null;
  /** The same pin under the hand-aimed track, when one is set. Shown beside pinEta. */
  pinEtaManual: EtaResult | null;
  /** Where the drag handle sits, so the map can hit-test it. */
  bearingHandle: LngLat | null;
  measureText: string | null;
  zoneEtas: Map<string, EtaResult>;
}

/**
 * Everything the overlay draws, in one pass. Kept together so the label text on
 * the map and the text in the panel can never drift apart.
 */
export function renderTools({ state, alert, now }: ToolRenderInput): ToolRender {
  const features: Feature[] = [];
  const zoneEtas = new Map<string, EtaResult>();
  let pinEta: EtaResult | null = null;
  let pinEtaManual: EtaResult | null = null;
  let bearingHandle: LngLat | null = null;
  let measureText: string | null = null;

  const geometry = alert?.geometry ?? null;
  const motion = alert?.motion ?? null;

  // --- ETA pin -------------------------------------------------------------
  if (state.pin) {
    pinEta = etaToPoint(geometry, motion, state.pin, now);
    // Same storm, same speed, aimed where the user pointed. Direction only: overriding
    // the speed as well would make the number a guess twice over.
    if (state.manualBearing !== null && motion) {
      pinEtaManual = etaToPoint(
        geometry,
        { ...motion, bearingDeg: state.manualBearing, source: 'manual', confidence: 'low' },
        state.pin,
        now,
      );
    }
    const shown = pinEtaManual ?? pinEta;
    if (pinEta.cone) {
      // The observed cone stays on screen underneath, so a hand-aimed track is always
      // seen against what the weather is actually doing rather than replacing it.
      features.push(feat(pinEta.cone as Feature['geometry'], { k: 'cone' }));
    }
    if (pinEtaManual?.cone) {
      features.push(feat(pinEtaManual.cone as Feature['geometry'], { k: 'cone-manual' }));
    }
    // The handle sits upwind, at the cone's tip: you drag it to where weather comes FROM.
    const handleBearing = state.manualBearing ?? motion?.bearingDeg ?? null;
    if (handleBearing !== null) {
      // Sit exactly at the cone's tip. A separate formula here is how the handle ended
      // up floating past the end of the cone it is supposed to be attached to.
      const reach = shown.coneReachMiles > 0 ? shown.coneReachMiles : Math.max(shown.distanceMiles, 12);
      bearingHandle = destination(state.pin, handleBearing + 180, reach);
      features.push(feat({ type: 'LineString', coordinates: [state.pin, bearingHandle] }, { k: 'aim' }));
      features.push(feat({ type: 'Point', coordinates: bearingHandle }, { k: 'handle' }));
    }
    features.push(feat({ type: 'Point', coordinates: state.pin }, { k: 'pin' }));
    const range = formatEtaRange(pinEta);
    features.push(
      feat(
        { type: 'Point', coordinates: state.pin },
        {
          k: 'label',
          label: range
            ? `${formatDistance(pinEta.distanceMiles)} · ${range.replace('in about ', '').replace('in ', '')}`
            : formatDistance(pinEta.distanceMiles),
        },
      ),
    );
  }

  // --- Measure line --------------------------------------------------------
  if (state.measure.length >= 1) {
    for (const p of state.measure) features.push(feat({ type: 'Point', coordinates: p }, { k: 'vert' }));
  }
  if (state.measure.length === 2) {
    const [a, b] = state.measure as [LngLat, LngLat];
    features.push(feat({ type: 'LineString', coordinates: [a, b] }, { k: 'measure' }));
    const r = etaAlongLine(a, b, motion, now);
    measureText =
      r.crossingMinutes === null
        ? `${r.miles} mi · ${r.km} km`
        : `${r.miles} mi · ${r.km} km · weather crosses in ~${r.crossingMinutes} min`;
    features.push(feat({ type: 'Point', coordinates: midpoint(a, b) }, { k: 'label', label: `${r.miles} mi` }));
  }

  // --- Zones ---------------------------------------------------------------
  for (const zone of state.zones) {
    const g = zoneGeometry(zone);
    if (!g) continue;
    const eta = etaToPointForZone(g, geometry, motion, now);
    if (eta) zoneEtas.set(zone.id, eta);
    const threatened = eta?.status === 'arriving' || eta?.status === 'inside';
    features.push(feat(g as Feature['geometry'], { k: 'zone', threat: threatened ? 1 : 0 }));
    const anchor = zone.kind === 'circle' ? zone.points[0]! : zone.points[0]!;
    const range = eta ? formatEtaRange(eta) : null;
    if (eta?.status === 'inside') {
      features.push(feat({ type: 'Point', coordinates: anchor }, { k: 'label', label: 'weather here now' }));
    } else if (range) {
      features.push(
        feat({ type: 'Point', coordinates: anchor }, { k: 'label', label: `enters ${range.replace('in about ', '')}` }),
      );
    }
  }

  // Zone in progress.
  if (state.pending.length) {
    for (const p of state.pending) features.push(feat({ type: 'Point', coordinates: p }, { k: 'vert' }));
    if (state.mode === 'zone-circle' && state.pending.length === 2) {
      const r = haversineMiles(state.pending[0]!, state.pending[1]!);
      features.push(
        feat({ type: 'Polygon', coordinates: [circleRing(state.pending[0]!, r)] } as Feature['geometry'], {
          k: 'zone',
          threat: 0,
        }),
      );
    } else if (state.pending.length >= 3) {
      features.push(
        feat({ type: 'Polygon', coordinates: [[...state.pending, state.pending[0]!]] } as Feature['geometry'], {
          k: 'zone',
          threat: 0,
        }),
      );
    } else if (state.pending.length === 2) {
      features.push(feat({ type: 'LineString', coordinates: state.pending }, { k: 'measure' }));
    }
  }

  // --- Markup --------------------------------------------------------------
  for (const s of state.strokes) {
    if (s.points.length < 2) continue;
    features.push(feat({ type: 'LineString', coordinates: s.points }, { k: 'markup' }));
  }

  return {
    features: features.length ? { type: 'FeatureCollection', features } : EMPTY,
    pinEta,
    pinEtaManual,
    bearingHandle,
    measureText,
    zoneEtas,
  };
}

/** Earliest arrival anywhere in the zone, via the shared sampler. */
function etaToPointForZone(
  zone: AreaGeometry,
  geometry: AreaGeometry | null,
  motion: StormMotion | null,
  now: string,
): EtaResult | null {
  if (!geometry || !motion) return null;
  return etaToZone(zone, motion, geometry, now);
}

/**
 * The alert whose weather reaches `point` soonest — what every tool reasons about.
 * Falls back to the nearest alert when nothing has a usable track, so the tools
 * still show distances when they cannot show times.
 */
export function primaryAlert(
  alerts: TenantAlert[],
  point: LngLat | null,
  now: string,
): { geometry: AreaGeometry | null; motion: StormMotion | null; event: string } | null {
  const withGeom = alerts.filter((ta) => ta.alert?.geometry);
  if (!withGeom.length) return null;
  if (!point) {
    const first = withGeom[0]!;
    return { geometry: first.alert.geometry, motion: first.alert.motion, event: first.alert.event };
  }
  let best = withGeom[0]!;
  let bestRank = Infinity;
  for (const ta of withGeom) {
    const r = etaToPoint(ta.alert.geometry, ta.alert.motion, point, now);
    const rank =
      r.status === 'inside' ? -1 : r.status === 'arriving' ? (r.minutesLow ?? 9_000) : 10_000 + r.distanceMiles;
    if (rank < bestRank) {
      bestRank = rank;
      best = ta;
    }
  }
  return { geometry: best.alert.geometry, motion: best.alert.motion, event: best.alert.event };
}
